import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import { TEMPLATE_CATEGORIES, PRODUCT_CATEGORIES, normalizeTemplate } from '../components/TemplatesPickerModal';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';

const UNITS = ['pc', 'ft', 'ea', 'lot', 'roll', 'bag', 'lb', 'gal'];

// ── Custom template form helpers ──
let itemRowCounter = 1;
const newItemRow = () => ({
  _key: itemRowCounter++,
  description: '',
  unit: 'pc',
  defaultQty: 1,
  defaultPrice: '',
  defaultCost: '',
});

const emptyForm = () => ({
  name: '',
  category: TEMPLATE_CATEGORIES[0],
  description: '',
  items: [newItemRow()],
});

const emptyStockForm = () => ({
  name: '',
  category: PRODUCT_CATEGORIES[0],
  description: '',
  items: [],
});

const computeBundleTotals = (items) => {
  const price = items.reduce((s, i) => s + ((parseFloat(i.defaultQty) || 0) * (parseFloat(i.defaultPrice) || 0)), 0);
  const cost = items.reduce((s, i) => s + ((parseFloat(i.defaultQty) || 0) * (parseFloat(i.defaultCost) || 0)), 0);
  const margin = price > 0 ? ((price - cost) / price) * 100 : null;
  return { price, cost, margin };
};

const marginColor = (m) => {
  if (m === null) return 'text-[#6B7C93]';
  if (m >= 30) return 'text-[#0F6E56]';
  if (m >= 15) return 'text-[#854F0B]';
  return 'text-[#A32D2D]';
};

function SpecialOrderTemplates({ user }) {
  const { userName: roleName } = useRole();
  const { products } = useData();
  const userName = roleName || user?.email?.split('@')[0]?.toUpperCase();

  const [activeTab, setActiveTab] = useState('custom');

  // ── Custom template state ──
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // ── Stock bundle state ──
  const [stockBundles, setStockBundles] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);
  const [stockSearch, setStockSearch] = useState('');
  const [stockFilterCategory, setStockFilterCategory] = useState('All');
  const [showStockForm, setShowStockForm] = useState(false);
  const [editingStockId, setEditingStockId] = useState(null);
  const [stockForm, setStockForm] = useState(emptyStockForm());
  const [savingStock, setSavingStock] = useState(false);
  const [stockExpandedId, setStockExpandedId] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchCategory, setProductSearchCategory] = useState('All');

  useEffect(() => { loadTemplates(); loadBundles(); }, []);

  // ── Custom template CRUD ──
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'specialOrderTemplates'));
      const list = snap.docs.map(d => normalizeTemplate({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTemplates(list);
    } catch (err) {
      console.error('Error loading templates:', err);
      alert('Error loading templates.');
    }
    setLoading(false);
  };

  const openNew = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setForm({
      name: t.name || '',
      category: t.category || TEMPLATE_CATEGORIES[0],
      description: t.description || '',
      items: (t.items || []).map(item => ({
        _key: itemRowCounter++,
        description: item.description || '',
        unit: item.unit || 'pc',
        defaultQty: item.defaultQty ?? 1,
        defaultPrice: item.defaultPrice ?? '',
        defaultCost: item.defaultCost ?? '',
      })),
    });
    setEditingId(t.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Please enter a template name.'); return; }
    const validItems = form.items.filter(i => i.description.trim());
    if (validItems.length === 0) { alert('Please add at least one item with a description.'); return; }
    setSaving(true);
    const now = new Date().toISOString();
    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim(),
      items: validItems.map((item, idx) => ({
        id: `item-${idx + 1}`,
        description: item.description.trim(),
        unit: item.unit || 'pc',
        defaultQty: parseFloat(item.defaultQty) || 1,
        defaultPrice: parseFloat(item.defaultPrice) || 0,
        defaultCost: parseFloat(item.defaultCost) || 0,
      })),
      updatedAt: now,
      updatedBy: userName,
    };
    try {
      if (editingId) {
        await updateDoc(doc(db, 'specialOrderTemplates', editingId), payload);
      } else {
        await addDoc(collection(db, 'specialOrderTemplates'), {
          ...payload, createdAt: now, createdBy: userName,
        });
      }
      await loadTemplates();
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Error saving template.');
    }
    setSaving(false);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?\n\nThis only removes the template — existing quotes/invoices that used it are unaffected.`)) return;
    try {
      await deleteDoc(doc(db, 'specialOrderTemplates', t.id));
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Error deleting template.');
    }
  };

  const handleDuplicate = async (t) => {
    const now = new Date().toISOString();
    try {
      await addDoc(collection(db, 'specialOrderTemplates'), {
        name: `${t.name} (Copy)`,
        category: t.category,
        description: t.description || '',
        items: (t.items || []).map((item, idx) => ({
          id: `item-${idx + 1}`,
          description: item.description,
          unit: item.unit,
          defaultQty: parseFloat(item.defaultQty) || 1,
          defaultPrice: parseFloat(item.defaultPrice) || 0,
          defaultCost: parseFloat(item.defaultCost) || 0,
        })),
        createdAt: now, createdBy: userName,
        updatedAt: now, updatedBy: userName,
      });
      await loadTemplates();
    } catch (err) {
      console.error('Error duplicating template:', err);
      alert('Error duplicating template.');
    }
  };

  const updateItemField = (key, field, value) => {
    setForm(f => ({ ...f, items: f.items.map(i => i._key === key ? { ...i, [field]: value } : i) }));
  };
  const addItemRow = () => setForm(f => ({ ...f, items: [...f.items, newItemRow()] }));
  const removeItemRow = (key) => setForm(f => ({
    ...f, items: f.items.length === 1 ? [newItemRow()] : f.items.filter(i => i._key !== key),
  }));

  // ── Stock bundle CRUD ──
  const loadBundles = async () => {
    setLoadingStock(true);
    try {
      const snap = await getDocs(collection(db, 'stockBundles'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setStockBundles(list);
    } catch (err) {
      console.error('Error loading stock bundles:', err);
      alert('Error loading stock bundles.');
    }
    setLoadingStock(false);
  };

  const openNewStock = () => {
    setStockForm(emptyStockForm());
    setEditingStockId(null);
    setProductSearch('');
    setShowStockForm(true);
  };

  const openEditStock = (b) => {
    setStockForm({
      name: b.name || '',
      category: b.category || PRODUCT_CATEGORIES[0],
      description: b.description || '',
      items: (b.items || []).map(item => ({
        id: item.id,
        productId: item.productId,
        sku: item.sku,
        description: item.description,
      })),
    });
    setEditingStockId(b.id);
    setProductSearch('');
    setShowStockForm(true);
  };

  const handleSaveStock = async () => {
    if (!stockForm.name.trim()) { alert('Please enter a bundle name.'); return; }
    if (stockForm.items.length === 0) { alert('Please add at least one product.'); return; }
    setSavingStock(true);
    const now = new Date().toISOString();
    const payload = {
      name: stockForm.name.trim(),
      category: stockForm.category,
      description: stockForm.description.trim(),
      items: stockForm.items.map((item, idx) => ({
        id: `item-${idx + 1}`,
        productId: item.productId,
        sku: item.sku,
        description: item.description,
      })),
      updatedAt: now,
      updatedBy: userName,
    };
    try {
      if (editingStockId) {
        await updateDoc(doc(db, 'stockBundles', editingStockId), payload);
      } else {
        await addDoc(collection(db, 'stockBundles'), {
          ...payload, createdAt: now, createdBy: userName,
        });
      }
      await loadBundles();
      setShowStockForm(false);
      setEditingStockId(null);
      setStockForm(emptyStockForm());
    } catch (err) {
      console.error('Error saving bundle:', err);
      alert('Error saving bundle.');
    }
    setSavingStock(false);
  };

  const handleDeleteStock = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"?\n\nThis only removes the bundle — existing quotes/invoices that used it are unaffected.`)) return;
    try {
      await deleteDoc(doc(db, 'stockBundles', b.id));
      await loadBundles();
    } catch (err) {
      console.error('Error deleting bundle:', err);
      alert('Error deleting bundle.');
    }
  };

  const handleDuplicateStock = async (b) => {
    const now = new Date().toISOString();
    try {
      await addDoc(collection(db, 'stockBundles'), {
        name: `${b.name} (Copy)`,
        category: b.category,
        description: b.description || '',
        items: (b.items || []).map((item, idx) => ({
          id: `item-${idx + 1}`,
          productId: item.productId,
          sku: item.sku,
          description: item.description,
        })),
        createdAt: now, createdBy: userName,
        updatedAt: now, updatedBy: userName,
      });
      await loadBundles();
    } catch (err) {
      console.error('Error duplicating bundle:', err);
      alert('Error duplicating bundle.');
    }
  };

  const addProductToBundle = (product) => {
    if (stockForm.items.some(i => i.productId === product.id)) return;
    setStockForm(f => ({
      ...f,
      items: [...f.items, {
        id: `tmp-${Date.now()}-${Math.random()}`,
        productId: product.id,
        sku: product.sku,
        description: product.description,
      }],
    }));
    setProductSearch('');
  };

  const removeProductFromBundle = (id) => {
    setStockForm(f => ({ ...f, items: f.items.filter(i => i.id !== id) }));
  };

  // ── Computed: filtering ──
  const filtered = templates.filter(t => {
    const term = search.trim().toLowerCase();
    const matchSearch = !term ||
      t.name?.toLowerCase().includes(term) ||
      t.description?.toLowerCase().includes(term) ||
      (t.items || []).some(i => i.description?.toLowerCase().includes(term));
    const matchCategory = filterCategory === 'All' || t.category === filterCategory;
    return matchSearch && matchCategory;
  });

  const filteredStock = stockBundles.filter(b => {
    const term = stockSearch.trim().toLowerCase();
    const matchSearch = !term ||
      b.name?.toLowerCase().includes(term) ||
      b.description?.toLowerCase().includes(term) ||
      (b.items || []).some(i => i.description?.toLowerCase().includes(term) || i.sku?.toLowerCase().includes(term));
    const matchCategory = stockFilterCategory === 'All' || b.category === stockFilterCategory;
    return matchSearch && matchCategory;
  });

  // Category options for the product picker, driven from the actual product
  // data so gate categories (whose stored strings may differ from the hardcoded
  // PRODUCT_CATEGORIES list) are always selectable and return results.
  const availableProductCategories = useMemo(() => {
    const present = new Set();
    products.forEach(p => { if (p.category) present.add(p.category.trim()); });
    const ordered = PRODUCT_CATEGORIES.filter(c => present.has(c));
    const extras = [...present].filter(c => !PRODUCT_CATEGORIES.includes(c)).sort();
    return [...ordered, ...extras];
  }, [products]);

  // Product search results for the bundle form
  const productSearchResults = products.filter(p => {
    if (p.isActive === false) return false;
    const term = productSearch.trim().toLowerCase();
    if (!term) return false;
    const matchTerm =
      p.description?.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term);
    const matchCat = productSearchCategory === 'All' || (p.category || '').trim() === productSearchCategory;
    return matchTerm && matchCat;
  });

  const formTotals = computeBundleTotals(form.items);

  return (
    <>
      <Sidebar />
      <div className="min-h-screen bg-[#CDE7F5]" style={{ marginLeft: SIDEBAR_W }}>

      <div className="p-6 max-w-6xl mx-auto">

        {/* Slim header row */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-xl font-bold text-[#03045E]">Special Order Templates</h1>
        </div>


        {/* Tabs */}
        <div className="bg-white rounded-xl border border-[#E3ECF5] p-4 mb-4">
          <div className="flex gap-2 border-b border-[#E3ECF5]">
            <button
              onClick={() => setActiveTab('custom')}
              className={`px-6 py-3 font-bold border-b-2 transition ${
                activeTab === 'custom'
                  ? 'border-[#0077B6] text-[#0077B6]'
                  : 'border-transparent text-[#6B7C93] hover:text-[#03045E]'
              }`}
            >
              📋 Custom Templates
            </button>
            <button
              onClick={() => setActiveTab('stock')}
              className={`px-6 py-3 font-bold border-b-2 transition ${
                activeTab === 'stock'
                  ? 'border-[#0077B6] text-[#0077B6]'
                  : 'border-transparent text-[#6B7C93] hover:text-[#03045E]'
              }`}
            >
              📦 Stock Bundles
            </button>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════
            CUSTOM TEMPLATES TAB
            ════════════════════════════════════════════════════════════ */}
        {activeTab === 'custom' && (
          <>
            <div className="bg-white rounded-xl border border-[#E3ECF5] p-4 mb-4 flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-[#03045E] mr-2">📋 Custom Templates</h2>
              <input
                type="text"
                placeholder="Search by name, description, or item..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-[200px] border border-[#E3ECF5] rounded-lg px-4 py-2 focus:outline-none focus:border-[#0077B6]"
              />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-[#E3ECF5] rounded-lg px-3 py-2 bg-white"
              >
                <option value="All">All Categories</option>
                {TEMPLATE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                onClick={openNew}
                className="bg-[#0077B6] hover:bg-[#0369A1] text-white font-bold px-6 py-2 rounded-lg whitespace-nowrap"
              >
                + New Template
              </button>
            </div>

            {loading ? (
              <p className="text-center py-12 text-[#6B7C93] bg-white rounded-xl border border-[#E3ECF5]">Loading…</p>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 text-[#6B7C93] bg-white rounded-xl border border-[#E3ECF5]">
                <p className="text-lg mb-1">No custom templates yet.</p>
                <p className="text-sm">Click "+ New Template" to create your first one.</p>
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center py-12 text-[#6B7C93] bg-white rounded-xl border border-[#E3ECF5]">No templates match your search.</p>
            ) : (
              <div className="space-y-3">
                {filtered.map(t => {
                  const { price, cost, margin } = computeBundleTotals(t.items || []);
                  const itemCount = (t.items || []).length;
                  const expanded = expandedId === t.id;
                  return (
                    <div key={t.id} className="bg-white rounded-xl border border-[#E3ECF5] overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => setExpandedId(expanded ? null : t.id)}
                          >
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-[#6B7C93] text-xs">{expanded ? '▼' : '▶'}</span>
                              <h3 className="font-bold text-[#03045E] text-lg">{t.name}</h3>
                              {t.category && (
                                <span className="bg-[#E6F1FB] text-[#0077B6] px-2 py-0.5 rounded text-xs font-bold">{t.category}</span>
                              )}
                              <span className="bg-[#CDE7F5] text-[#6B7C93] px-2 py-0.5 rounded text-xs font-bold">
                                📦 {itemCount} item{itemCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {t.description && (
                              <p className="text-[#6B7C93] text-sm mt-1">{t.description}</p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-[#0077B6] font-bold text-lg">${price.toFixed(2)}</p>
                            <p className="text-[#854F0B] text-xs">Cost ${cost.toFixed(2)}</p>
                            {margin !== null && (
                              <p className={`text-xs font-bold ${marginColor(margin)}`}>{margin.toFixed(1)}% margin</p>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={() => openEdit(t)} className="bg-[#E6F1FB] hover:bg-[#D6E9F8] text-[#0077B6] px-3 py-2 rounded text-sm font-bold" title="Edit">✏️ Edit</button>
                            <button onClick={() => handleDuplicate(t)} className="bg-[#E0F7FA] hover:bg-[#CDEEF5] text-[#0096C7] px-3 py-2 rounded text-sm font-bold" title="Duplicate">📋 Copy</button>
                            <button onClick={() => handleDelete(t)} className="bg-[#FBECEC] hover:bg-[#F5D9D9] text-[#A32D2D] px-3 py-2 rounded text-sm font-bold" title="Delete">🗑️</button>
                          </div>
                        </div>
                      </div>
                      {expanded && (
                        <div className="bg-[#CDE7F5] border-t border-[#E3ECF5] px-4 py-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-[#6B7C93] uppercase tracking-wide">
                                <th className="py-1 pr-2">#</th>
                                <th className="py-1 pr-2">Description</th>
                                <th className="py-1 pr-2 text-center">Unit</th>
                                <th className="py-1 pr-2 text-center">Qty</th>
                                <th className="py-1 pr-2 text-right">Price</th>
                                <th className="py-1 pr-2 text-right">Subtotal</th>
                                <th className="py-1 text-right">Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(t.items || []).map((item, i) => {
                                const q = parseFloat(item.defaultQty) || 0;
                                const p = parseFloat(item.defaultPrice) || 0;
                                const c = parseFloat(item.defaultCost) || 0;
                                return (
                                  <tr key={item.id || i} className="border-t border-[#E3ECF5]">
                                    <td className="py-1 pr-2 text-[#6B7C93]">{i + 1}</td>
                                    <td className="py-1 pr-2 text-[#03045E]">{item.description}</td>
                                    <td className="py-1 pr-2 text-center text-[#6B7C93]">{item.unit}</td>
                                    <td className="py-1 pr-2 text-center text-[#03045E]">{q}</td>
                                    <td className="py-1 pr-2 text-right text-[#0077B6]">${p.toFixed(2)}</td>
                                    <td className="py-1 pr-2 text-right font-bold text-[#03045E]">${(q * p).toFixed(2)}</td>
                                    <td className="py-1 text-right text-[#854F0B]">${(q * c).toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════
            STOCK BUNDLES TAB
            ════════════════════════════════════════════════════════════ */}
        {activeTab === 'stock' && (
          <>
            <div className="bg-white rounded-xl border border-[#E3ECF5] p-4 mb-4 flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-[#03045E] mr-2">📦 Stock Bundles</h2>
              <input
                type="text"
                placeholder="Search by name, description, or SKU..."
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
                className="flex-1 min-w-[200px] border border-[#E3ECF5] rounded-lg px-4 py-2 focus:outline-none focus:border-[#0077B6]"
              />
              <select
                value={stockFilterCategory}
                onChange={(e) => setStockFilterCategory(e.target.value)}
                className="border border-[#E3ECF5] rounded-lg px-3 py-2 bg-white"
              >
                <option value="All">All Categories</option>
                {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                onClick={openNewStock}
                className="bg-[#0077B6] hover:bg-[#0369A1] text-white font-bold px-6 py-2 rounded-lg whitespace-nowrap"
              >
                + New Stock Bundle
              </button>
            </div>

            <div className="bg-[#E6F1FB] border border-[#E3ECF5] rounded-lg p-3 mb-4 text-sm text-[#03045E] flex items-start gap-2">
              <span>ℹ️</span>
              <span>
                Stock bundles reference real products from the price list. Prices are pulled live based on the current Price Level (A/B/C) when added to a quote — no prices are stored on the bundle.
              </span>
            </div>

            {loadingStock ? (
              <p className="text-center py-12 text-[#6B7C93] bg-white rounded-xl border border-[#E3ECF5]">Loading…</p>
            ) : stockBundles.length === 0 ? (
              <div className="text-center py-12 text-[#6B7C93] bg-white rounded-xl border border-[#E3ECF5]">
                <p className="text-lg mb-1">No stock bundles yet.</p>
                <p className="text-sm">Click "+ New Stock Bundle" to create your first one.</p>
              </div>
            ) : filteredStock.length === 0 ? (
              <p className="text-center py-12 text-[#6B7C93] bg-white rounded-xl border border-[#E3ECF5]">No bundles match your search.</p>
            ) : (
              <div className="space-y-3">
                {filteredStock.map(b => {
                  const itemCount = (b.items || []).length;
                  const expanded = stockExpandedId === b.id;
                  return (
                    <div key={b.id} className="bg-white rounded-xl border border-[#E3ECF5] overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => setStockExpandedId(expanded ? null : b.id)}
                          >
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-[#6B7C93] text-xs">{expanded ? '▼' : '▶'}</span>
                              <h3 className="font-bold text-[#03045E] text-lg">{b.name}</h3>
                              {b.category && (
                                <span className="bg-[#E6F1FB] text-[#0077B6] px-2 py-0.5 rounded text-xs font-bold">{b.category}</span>
                              )}
                              <span className="bg-[#CDE7F5] text-[#6B7C93] px-2 py-0.5 rounded text-xs font-bold">
                                📦 {itemCount} item{itemCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {b.description && (
                              <p className="text-[#6B7C93] text-sm mt-1">{b.description}</p>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={() => openEditStock(b)} className="bg-[#E6F1FB] hover:bg-[#D6E9F8] text-[#0077B6] px-3 py-2 rounded text-sm font-bold" title="Edit">✏️ Edit</button>
                            <button onClick={() => handleDuplicateStock(b)} className="bg-[#E0F7FA] hover:bg-[#CDEEF5] text-[#0096C7] px-3 py-2 rounded text-sm font-bold" title="Duplicate">📋 Copy</button>
                            <button onClick={() => handleDeleteStock(b)} className="bg-[#FBECEC] hover:bg-[#F5D9D9] text-[#A32D2D] px-3 py-2 rounded text-sm font-bold" title="Delete">🗑️</button>
                          </div>
                        </div>
                      </div>
                      {expanded && (
                        <div className="bg-[#CDE7F5] border-t border-[#E3ECF5] px-4 py-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-[#6B7C93] uppercase tracking-wide">
                                <th className="py-1 pr-2">#</th>
                                <th className="py-1 pr-2">SKU</th>
                                <th className="py-1 pr-2">Description</th>
                                <th className="py-1 text-right">Current Price (A)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(b.items || []).map((item, i) => {
                                const product = products.find(p => p.id === item.productId);
                                const currentPrice = product ? parseFloat(product.priceA) || 0 : null;
                                return (
                                  <tr key={item.id || i} className="border-t border-[#E3ECF5]">
                                    <td className="py-1 pr-2 text-[#6B7C93]">{i + 1}</td>
                                    <td className="py-1 pr-2 font-mono text-[#0077B6] text-xs font-bold">{item.sku}</td>
                                    <td className="py-1 pr-2 text-[#03045E]">
                                      {item.description}
                                      {!product && <span className="ml-2 text-[#A32D2D] text-xs font-bold">⚠️ product deleted</span>}
                                    </td>
                                    <td className="py-1 text-right text-[#0077B6] font-bold">
                                      {currentPrice !== null ? `$${currentPrice.toFixed(2)}` : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

      </div>

      {/* ════════════════════════════════════════════════════════════
          CUSTOM TEMPLATE FORM MODAL
          ════════════════════════════════════════════════════════════ */}
      {showForm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(3, 4, 94, 0.35)' }}
          onClick={() => !saving && setShowForm(false)}
        >
          <div
            className="bg-white rounded-xl border border-[#E3ECF5] w-full max-w-5xl max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#E3ECF5] flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#03045E]">
                {editingId ? '✏️ Edit Custom Template' : '+ New Custom Template'}
              </h3>
              <button onClick={() => !saving && setShowForm(false)} className="text-[#6B7C93] hover:text-[#A32D2D] text-2xl leading-none">✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#03045E] font-semibold mb-1 text-sm">Template Name <span className="text-[#A32D2D]">*</span></label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. 4' Aluminum Fence Complete"
                    className="w-full border border-[#E3ECF5] rounded-lg px-3 py-2 focus:outline-none focus:border-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-[#03045E] font-semibold mb-1 text-sm">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-[#E3ECF5] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#0077B6]"
                  >
                    {TEMPLATE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[#03045E] font-semibold mb-1 text-sm">Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What is this bundle for? (shown in the picker, not on the customer-facing document.)"
                  rows={2}
                  className="w-full border border-[#E3ECF5] rounded-lg px-3 py-2 focus:outline-none focus:border-[#0077B6]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-[#03045E]">Bundle Items</h4>
                  <button
                    onClick={addItemRow}
                    className="bg-[#0077B6] hover:bg-[#0369A1] text-white font-bold text-sm px-4 py-1.5 rounded-lg"
                  >
                    + Add Item
                  </button>
                </div>
                <div className="overflow-x-auto border border-[#E3ECF5] rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-[#CDE7F5] border-b border-[#E3ECF5]">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs text-[#6B7C93] w-8">#</th>
                        <th className="px-2 py-2 text-left text-xs text-[#6B7C93]">Description <span className="text-[#A32D2D]">*</span></th>
                        <th className="px-2 py-2 text-left text-xs text-[#6B7C93] w-24">Unit</th>
                        <th className="px-2 py-2 text-center text-xs text-[#6B7C93] w-20">Qty</th>
                        <th className="px-2 py-2 text-right text-xs text-[#6B7C93] w-28">Price</th>
                        <th className="px-2 py-2 text-right text-xs text-[#6B7C93] w-28">Cost</th>
                        <th className="px-2 py-2 text-center text-xs text-[#6B7C93] w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, i) => (
                        <tr key={item._key} className="border-t border-[#E3ECF5]">
                          <td className="px-2 py-2 text-[#6B7C93] text-center">{i + 1}</td>
                          <td className="px-2 py-2">
                            <input type="text" value={item.description}
                              onChange={(e) => updateItemField(item._key, 'description', e.target.value)}
                              placeholder="e.g. Aluminum Panel 4' x 6' Black"
                              className="w-full border border-[#E3ECF5] rounded px-2 py-1 focus:outline-none focus:border-[#0077B6]" />
                          </td>
                          <td className="px-2 py-2">
                            <select value={item.unit}
                              onChange={(e) => updateItemField(item._key, 'unit', e.target.value)}
                              className="w-full border border-[#E3ECF5] rounded px-2 py-1 bg-white focus:outline-none focus:border-[#0077B6]">
                              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" value={item.defaultQty}
                              onChange={(e) => updateItemField(item._key, 'defaultQty', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              min="0" step="0.01"
                              className="w-full border border-[#E3ECF5] rounded px-2 py-1 text-center focus:outline-none focus:border-[#0077B6]" />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" value={item.defaultPrice}
                              onChange={(e) => updateItemField(item._key, 'defaultPrice', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              placeholder="0.00" min="0" step="0.01"
                              className="w-full border border-[#E3ECF5] rounded px-2 py-1 text-right focus:outline-none focus:border-[#0077B6]" />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" value={item.defaultCost}
                              onChange={(e) => updateItemField(item._key, 'defaultCost', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              placeholder="0.00" min="0" step="0.01"
                              className="w-full border border-[#E3ECF5] rounded px-2 py-1 text-right focus:outline-none focus:border-[#0077B6]" />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button onClick={() => removeItemRow(item._key)}
                              className="text-[#A32D2D] hover:text-[#8A2525] text-lg leading-none"
                              title="Remove item">✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-[#CDE7F5] rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-[#6B7C93] text-xs uppercase tracking-wide">Items</p>
                  <p className="font-bold text-[#03045E] text-lg">{form.items.filter(i => i.description.trim()).length}</p>
                </div>
                <div>
                  <p className="text-[#6B7C93] text-xs uppercase tracking-wide">Total Price</p>
                  <p className="font-bold text-[#0077B6] text-lg">${formTotals.price.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[#6B7C93] text-xs uppercase tracking-wide">Total Cost</p>
                  <p className="font-bold text-[#854F0B] text-lg">${formTotals.cost.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[#6B7C93] text-xs uppercase tracking-wide">Margin</p>
                  <p className={`font-bold text-lg ${marginColor(formTotals.margin)}`}>
                    {formTotals.margin === null ? '—' : `${formTotals.margin.toFixed(1)}%`}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-[#E3ECF5] flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} disabled={saving}
                className="bg-white hover:bg-[#CDE7F5] border border-[#E3ECF5] text-[#03045E] font-bold px-5 py-2 rounded-lg disabled:opacity-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="bg-[#0077B6] hover:bg-[#0369A1] text-white font-bold px-6 py-2 rounded-lg disabled:opacity-50">
                {saving ? 'Saving…' : '💾 Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
          STOCK BUNDLE FORM MODAL
          ════════════════════════════════════════════════════════════ */}
      {showStockForm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(3, 4, 94, 0.35)' }}
          onClick={() => !savingStock && setShowStockForm(false)}
        >
          <div
            className="bg-white rounded-xl border border-[#E3ECF5] w-full max-w-5xl max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#E3ECF5] flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#03045E]">
                {editingStockId ? '✏️ Edit Stock Bundle' : '+ New Stock Bundle'}
              </h3>
              <button onClick={() => !savingStock && setShowStockForm(false)} className="text-[#6B7C93] hover:text-[#A32D2D] text-2xl leading-none">✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#03045E] font-semibold mb-1 text-sm">Bundle Name <span className="text-[#A32D2D]">*</span></label>
                  <input
                    type="text"
                    value={stockForm.name}
                    onChange={(e) => setStockForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. PVC Order Package"
                    className="w-full border border-[#E3ECF5] rounded-lg px-3 py-2 focus:outline-none focus:border-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-[#03045E] font-semibold mb-1 text-sm">Category</label>
                  <select
                    value={stockForm.category}
                    onChange={(e) => setStockForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-[#E3ECF5] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#0077B6]"
                  >
                    {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[#03045E] font-semibold mb-1 text-sm">Description (optional)</label>
                <textarea
                  value={stockForm.description}
                  onChange={(e) => setStockForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Standard package contents and use case."
                  rows={2}
                  className="w-full border border-[#E3ECF5] rounded-lg px-3 py-2 focus:outline-none focus:border-[#0077B6]"
                />
              </div>

              {/* Product search */}
              <div className="bg-[#CDE7F5] rounded-lg p-3 border border-[#E3ECF5]">
                <label className="block text-[#03045E] font-semibold mb-2 text-sm">+ Add Product to Bundle</label>
                <div className="flex gap-3 flex-wrap">
                  <select
                    value={productSearchCategory}
                    onChange={(e) => setProductSearchCategory(e.target.value)}
                    className="border border-[#E3ECF5] rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="All">All Categories</option>
                    {availableProductCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="Search by description or SKU..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="flex-1 min-w-[200px] border border-[#E3ECF5] rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#0077B6]"
                  />
                </div>

                {productSearch && (
                  <div className="mt-2 bg-white border border-[#E3ECF5] rounded-lg max-h-48 overflow-y-auto">
                    {productSearchResults.length === 0 ? (
                      <p className="px-3 py-3 text-[#6B7C93] text-sm">No products match.</p>
                    ) : (
                      productSearchResults.slice(0, 20).map(p => {
                        const already = stockForm.items.some(i => i.productId === p.id);
                        return (
                          <div
                            key={p.id}
                            onClick={() => !already && addProductToBundle(p)}
                            className={`px-3 py-2 border-b border-[#E3ECF5] ${
                              already ? 'bg-[#CDE7F5] cursor-not-allowed opacity-60' : 'hover:bg-[#E6F1FB] cursor-pointer'
                            }`}
                          >
                            <p className="font-semibold text-sm">{p.description}</p>
                            <p className="text-xs text-[#6B7C93]">
                              SKU: {p.sku} | {p.category} | A: ${(parseFloat(p.priceA) || 0).toFixed(2)}
                              {already && <span className="ml-2 text-[#A32D2D] font-bold">Already added</span>}
                            </p>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Bundle items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-[#03045E]">Products in this Bundle ({stockForm.items.length})</h4>
                </div>
                <div className="bg-white border border-[#E3ECF5] rounded-lg overflow-hidden">
                  {stockForm.items.length === 0 ? (
                    <p className="text-center py-6 text-[#6B7C93]">No products added yet. Search above to add.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-[#E6F1FB]">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs text-[#6B7C93] w-10">#</th>
                          <th className="px-3 py-2 text-left text-xs text-[#6B7C93] w-32">SKU</th>
                          <th className="px-3 py-2 text-left text-xs text-[#6B7C93]">Description</th>
                          <th className="px-3 py-2 text-center text-xs text-[#6B7C93] w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockForm.items.map((item, i) => (
                          <tr key={item.id} className="border-t border-[#E3ECF5]">
                            <td className="px-3 py-2 text-[#6B7C93]">{i + 1}</td>
                            <td className="px-3 py-2 font-mono text-[#0077B6] text-xs font-bold">{item.sku}</td>
                            <td className="px-3 py-2 text-[#03045E]">{item.description}</td>
                            <td className="px-3 py-2 text-center">
                              <button onClick={() => removeProductFromBundle(item.id)}
                                className="text-[#A32D2D] hover:text-[#8A2525] text-lg leading-none"
                                title="Remove">✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <p className="text-xs text-[#6B7C93] mt-2 italic">
                  No qty/price stored here — prices come from the price list when added to a quote (current Price Level).
                </p>
              </div>
            </div>

            <div className="p-5 border-t border-[#E3ECF5] flex justify-end gap-3">
              <button onClick={() => setShowStockForm(false)} disabled={savingStock}
                className="bg-white hover:bg-[#CDE7F5] border border-[#E3ECF5] text-[#03045E] font-bold px-5 py-2 rounded-lg disabled:opacity-50">Cancel</button>
              <button onClick={handleSaveStock} disabled={savingStock}
                className="bg-[#0077B6] hover:bg-[#0369A1] text-white font-bold px-6 py-2 rounded-lg disabled:opacity-50">
                {savingStock ? 'Saving…' : '💾 Save Bundle'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default SpecialOrderTemplates;
